import React from 'react'

import {
  AlignItems,
  AlignSelf,
  Body2,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading6,
  JustifyContent,
  Overflow,
} from '@gousto-internal/citrus-react'
import type { UseByRecipeListItem } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { activateOnKeyboard } from '../keyboard'
import { ContainedImage, PremiumBadge, RTHBadge } from '../RecipeItemElements'
import { LoadingSkeleton } from './LoadingSkeleton'

type RecipeGroupedItemDetailsProps = {
  item: UseByRecipeListItem
  isPending: boolean
  showSkeleton: boolean
  onClickRecipe: (id: RecipeUUID) => void
}

const getDefaultImage = (item: UseByRecipeListItem) => {
  const sortedCrops = item.image.crops.slice().sort((a, b) => b.width - a.width)
  const midpointInArray = Math.floor(sortedCrops.length / 2)

  return sortedCrops[midpointInArray]?.url || ''
}

const getSrcSet = (item: UseByRecipeListItem) =>
  item.image.crops.map((crop) => `${crop.url} ${crop.width}w`).join(', ')

const getRecipeDetailsAccessibleName = (item: UseByRecipeListItem) =>
  [
    item.displayType === 'ready-to-heat' ? 'HEAT & EAT' : undefined,
    item.title,
    ...item.tagGroups.subtitle.map((tag) => tag.accessibleText),
    item.pricing && !item.pricing.isNegative ? 'PREMIUM' : undefined,
    item.pricing?.perPortionSurcharge,
  ]
    .filter(Boolean)
    .join(', ')

const RecipeGroupedPricing = ({
  item,
  showSkeleton,
}: {
  item: UseByRecipeListItem
  showSkeleton: boolean
}) => {
  if (!item.pricing) {
    return null
  }

  if (showSkeleton) {
    return (
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        gap={2}
        data-testid="recipe-grouped-item-pricing-pending"
        style={{ paddingTop: 4 }}
      >
        {!item.pricing.isNegative && <PremiumBadge>PREMIUM</PremiumBadge>}
        <LoadingSkeleton width="108px" />
      </Box>
    )
  }

  return (
    <Box
      display={Display.Flex}
      alignItems={AlignItems.Center}
      gap={2}
      data-testid="recipe-grouped-item-pricing"
      style={{ paddingTop: 4 }}
    >
      {!item.pricing.isNegative && <PremiumBadge>PREMIUM</PremiumBadge>}
      <Body2 color={Color.ColdGrey_800}>{item.pricing.perPortionSurcharge}</Body2>
    </Box>
  )
}

const getSubtitleSkeletonWidth = (index: number) => (index === 0 ? '52px' : '72px')

const RecipeGroupedDisplayTypeTag = ({ isReadyToHeat }: { isReadyToHeat: boolean }) => {
  if (!isReadyToHeat) {
    return null
  }

  return <RTHBadge>HEAT &amp; EAT</RTHBadge>
}

const RecipeGroupedSubtitle = ({
  item,
  showSkeleton,
}: {
  item: UseByRecipeListItem
  showSkeleton: boolean
}) => {
  if (item.tagGroups.subtitle.length === 0) {
    return null
  }

  if (showSkeleton) {
    return (
      <Box
        data-testid="recipe-grouped-item-subtitle-pending"
        display={Display.Flex}
        alignItems={AlignItems.Center}
        gap={2}
        style={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0 }}
      >
        {item.tagGroups.subtitle.map((tag, index) => (
          <LoadingSkeleton
            key={`${tag.type}-${tag.text}`}
            width={getSubtitleSkeletonWidth(index)}
            height={['20px', '24px']}
          />
        ))}
      </Box>
    )
  }

  return (
    <Box
      data-testid="recipe-grouped-item-subtitle"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      gap={2}
      style={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0 }}
    >
      {item.tagGroups.subtitle.map((tag, index) => (
        <React.Fragment key={`${tag.type}-${tag.text}`}>
          {index > 0 && (
            <Body2 aria-hidden="true" color={Color.ColdGrey_600}>
              •
            </Body2>
          )}
          <Body2 color={Color.ColdGrey_600} style={{ whiteSpace: 'nowrap' }}>
            {tag.text}
          </Body2>
        </React.Fragment>
      ))}
    </Box>
  )
}

export const RecipeGroupedItemDetails = ({
  item,
  isPending,
  showSkeleton,
  onClickRecipe,
}: RecipeGroupedItemDetailsProps) => {
  const isReadyToHeat = item.displayType === 'ready-to-heat'
  const handleRecipeClick = () => {
    if (isPending) {
      return
    }

    onClickRecipe(item.id)
  }
  const handleRecipeKeyDown = (event: React.KeyboardEvent) => {
    if (isPending) {
      return
    }

    activateOnKeyboard(event, handleRecipeClick)
  }

  return (
    <Box
      display={Display.Flex}
      flexGrow={1}
      flexShrink={1}
      minWidth="0"
      data-testid="recipe-grouped-item-details-action"
      aria-label={isPending ? undefined : getRecipeDetailsAccessibleName(item)}
      onClick={handleRecipeClick}
      onKeyDown={handleRecipeKeyDown}
      role={isPending ? undefined : 'button'}
      tabIndex={isPending ? undefined : 0}
      style={{ cursor: isPending ? 'default' : 'pointer' }}
    >
      <Box
        width="110px"
        minWidth="110px"
        minHeight="108px"
        alignSelf={AlignSelf.Stretch}
        overflowX={Overflow.Hidden}
        overflowY={Overflow.Hidden}
      >
        <ContainedImage
          alt={item.image.altText || item.title}
          loading="lazy"
          src={getDefaultImage(item)}
          srcSet={getSrcSet(item)}
          height="100%"
          width="110px"
        />
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        flexGrow={1}
        flexShrink={1}
        minWidth="0"
        paddingH={3}
        paddingV={2}
      >
        <RecipeGroupedDisplayTypeTag isReadyToHeat={isReadyToHeat} />
        <Heading6
          style={
            isReadyToHeat
              ? {
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  marginBottom: 4,
                }
              : {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                  marginBottom: 4,
                }
          }
        >
          {item.title}
        </Heading6>
        <RecipeGroupedSubtitle item={item} showSkeleton={showSkeleton} />
        <RecipeGroupedPricing item={item} showSkeleton={showSkeleton} />
      </Box>
    </Box>
  )
}
